import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { AlertsService } from '@tablesready/frontend-alerts-bar';
import {
  PARTY_HIGHLIGHT_QUERY_PARAM,
  PARTY_HIGHLIGHT_ROW_ID_PREFIX,
  PARTY_NOT_FOUND_MESSAGE,
  PartyNavigationService,
} from '@tablesready/frontend-party-info-compact';

export const PARTY_HIGHLIGHT_DURATION_MS = 10_000;

@Injectable()
export class PartyHighlightService implements OnDestroy {
  readonly highlightedPartyId$: Observable<string | null>;

  private readonly _highlightedPartyId$ = new BehaviorSubject<string | null>(null);
  private readonly alertedIds = new Set<string>();
  private readonly scrolledIds = new Set<string>();
  private readonly destroy$ = new Subject<void>();
  private observation?: Subscription;
  private clearTimer: ReturnType<typeof setTimeout> | null = null;
  // Prevents re-evaluation after highlight is consumed. Needed because
  // clearQueryParam() uses Location.replaceState() (bypasses Angular router)
  // so route.queryParamMap stays stale with the old partyId — without this
  // guard, every subsequent parties$ emission would re-trigger the highlight.
  // Reset in the partyId$ tap whenever queryParamMap re-emits a non-null
  // partyId, so a notification "View" re-click can re-fire the highlight.
  private consumed = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly alertsService: AlertsService,
    private readonly partyNavigation: PartyNavigationService,
  ) {
    this.highlightedPartyId$ = this._highlightedPartyId$.pipe(distinctUntilChanged());
  }

  get highlightedPartyId(): string | null {
    return this._highlightedPartyId$.value;
  }

  rowIdFor(partyId: string): string | null {
    return partyId === this.highlightedPartyId
      ? `${PARTY_HIGHLIGHT_ROW_ID_PREFIX}${partyId}`
      : null;
  }

  shouldSkipScrollToTop(): boolean {
    return this.route.snapshot.queryParamMap.has(PARTY_HIGHLIGHT_QUERY_PARAM);
  }

  getPartyIdFromUrl(): string | null {
    return this.route.snapshot.queryParamMap.get(PARTY_HIGHLIGHT_QUERY_PARAM)?.trim() || null;
  }

  /**
   * Drive highlight + scroll + not-found alerts from two reactive sources:
   *  - the `?partyId=` query param
   *  - `parties$`, the page's currently-rendered party list
   *
   * `parties$` must emit only after a real data load (use a `Subject`, not a
   * `BehaviorSubject` seeded with an empty array) so the first evaluation
   * doesn't race the page's initial fetch.
   *
   * Pages re-feed `parties$` whenever they bump pagination in response to
   * `highlightedPartyId$`; that re-entry terminates because `_highlightedPartyId$`
   * is `distinctUntilChanged`-gated, so a repeated id won't refire the
   * pagination subscription.
   */
  observe<T extends { _id: string }>(parties$: Observable<readonly T[]>): void {
    this.observation?.unsubscribe();

    // No `distinctUntilChanged` here: queryParamMap re-emits on every
    // navigation (it's a BehaviorSubject), and a re-click on the notification
    // "View" button — even for the same partyId — must be treated as a fresh
    // request. `clearQueryParam()` uses `Location.replaceState` which doesn't
    // round-trip the value through queryParamMap, so a duplicate emission is
    // the only signal we'll get that the user re-asked for this highlight.
    // The `tap` resets state so evaluate() runs as if it were brand new.
    // Re-fires from `parties$` (pagination bump, websocket updates) are still
    // gated by `consumed`, which evaluate() sets back to true on each pass.
    // Two sources of highlight requests, merged into one stream:
    //   1) queryParamMap — fires on cross-page navigations (URL actually changes)
    //   2) PartyNavigationService.highlightRequested$ — fires on every "View" click,
    //      including same-URL re-clicks where queryParamMap stays silent because
    //      Angular's ActivatedRoute only emits on real param changes. This is the
    //      only reliable trigger for the multiple-inbound-same-party case.
    //
    // The shared `tap` resets `consumed`, clears `scrolledIds`, and pushes a
    // `null` to `_highlightedPartyId$` so `evaluate()` always sees the request
    // as a brand-new highlight regardless of which source fired.
    const partyId$ = merge(
      this.route.queryParamMap.pipe(
        map((params) => params.get(PARTY_HIGHLIGHT_QUERY_PARAM)?.trim() || null),
      ),
      this.partyNavigation.highlightRequested$,
    ).pipe(
      tap((id) => {
        if (id) {
          this.consumed = false;
          this.scrolledIds.delete(id);
          this._highlightedPartyId$.next(null);
        }
      }),
    );

    // Stop observing the moment the user navigates away from this page.
    // NavigationStart fires before the URL is committed, so route.queryParamMap
    // never emits the destination's ?partyId on this page's subscription —
    // which would otherwise cause a spurious "party not found" alert when the
    // partyId belongs to a party that lives on the destination page, not here.
    //
    // Same-base-path navigations (e.g. /waitlist → /waitlist?partyId=xxx) are
    // intentionally excluded: Ionic may not fire ionViewWillLeave/ionViewWillEnter
    // for same-route navigations, so observe() would never be re-called. If we
    // killed the observation here the new partyId would be silently dropped.
    const currentPath = (this.router.url ?? '').split('?')[0];
    const navigationAway$ = this.router.events.pipe(
      filter((e): e is NavigationStart =>
        e instanceof NavigationStart && e.url.split('?')[0] !== currentPath),
      take(1),
    );

    this.observation = combineLatest([partyId$, parties$])
      .pipe(takeUntil(merge(this.destroy$, navigationAway$)))
      .subscribe(([id, parties]) => this.evaluate(id, parties));
  }

  clear(): void {
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
    this.observation?.unsubscribe();
    this.alertedIds.clear();
    this.scrolledIds.clear();
    this.consumed = false;
    this._highlightedPartyId$.next(null);
  }

  ngOnDestroy(): void {
    this.clear();
    this.destroy$.next();
    this.destroy$.complete();
    this._highlightedPartyId$.complete();
  }

  /**
   * The highlight is cleared only by the auto-clear timer (after
   * `PARTY_HIGHLIGHT_DURATION_MS`), never by a `null` URL transition — once we
   * consume a `partyId` we strip it from the URL, and that strip must not
   * cascade into clearing the highlight prematurely.
   *
   * Side effects that should fire once per activation (timer, URL strip on the
   * happy path) are gated on `isNewHighlight`; the not-found path strips the
   * URL too, gated on `alertedIds` so a single distinct id can't double-strip.
   * Scroll scheduling deliberately is NOT gated — the page re-emits `parties$`
   * after bumping pagination, and we want every re-emit to retry
   * `scheduleScrollIntoView` until the row is actually in the DOM
   * (`scrolledIds.add` only happens inside the rAF when `getElementById`
   * succeeds, so failed attempts don't poison the retry set).
   */
  private evaluate<T extends { _id: string }>(id: string | null, parties: readonly T[]): void {
    if (!id || this.consumed) {
      return;
    }

    const isOnPage = parties.some((p) => p._id === id);

    if (isOnPage) {
      const isNewHighlight = this._highlightedPartyId$.value !== id;
      if (isNewHighlight) {
        this.scrolledIds.clear();
      }
      this._highlightedPartyId$.next(id);
      if (!this.scrolledIds.has(id)) {
        this.scheduleScrollIntoView(id);
      }
      if (isNewHighlight) {
        this.consumed = true;
        this.scheduleHighlightClear();
        this.clearQueryParam();
      }
      return;
    }

    if (!this.alertedIds.has(id)) {
      this.alertedIds.add(id);
      this.alertsService.addWarning(PARTY_NOT_FOUND_MESSAGE, 'Info:');
      this.clearQueryParam();
    }
  }

  private scheduleHighlightClear(): void {
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
    }
    this.clearTimer = setTimeout(() => {
      this.clearTimer = null;
      this._highlightedPartyId$.next(null);
    }, PARTY_HIGHLIGHT_DURATION_MS);
  }

  // Uses Location.replaceState() instead of router.navigate() so the browser
  // URL is updated without firing Angular router events. router.navigate() on
  // the same route (just removing the query param) causes Ionic to fire
  // ionViewWillLeave + ionViewWillEnter, which clears the highlight and
  // reloads the page immediately after it was set.
  private clearQueryParam(): void {
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { [PARTY_HIGHLIGHT_QUERY_PARAM]: null },
      queryParamsHandling: 'merge',
    });
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  /**
   * Scroll the row into view, centred when it fits and top-aligned when it
   * doesn't. If the row isn't in the DOM yet (e.g. the page caps a paginated
   * list and the target sits past the cut-off) this is a no-op; the page is
   * expected to bump pagination on the next `highlightedPartyId$` emission,
   * which re-triggers `evaluate` and lands us back here once the row has been
   * rendered.
   *
   * The top of the row is what identifies the party (the name, the date), so it
   * is never the part left out: a record taller than the space it scrolls in —
   * a stacked card on a phone, with its messages and actions — is lined up by
   * its top and loses its bottom instead of being centred and losing both ends.
   */
  private scheduleScrollIntoView(partyId: string): void {
    const elementId = `${PARTY_HIGHLIGHT_ROW_ID_PREFIX}${partyId}`;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.getElementById(elementId);
      if (!el) return;

      this.scrolledIds.add(partyId);

      const rect = el.getBoundingClientRect();
      const bounds = visibleBounds(el);
      if (rect.top >= bounds.top && rect.bottom <= bounds.bottom) return;

      const fits = rect.height <= bounds.bottom - bounds.top;
      el.scrollIntoView({ block: fits ? 'center' : 'start', behavior: 'instant' as ScrollBehavior });

      // `start` lines the row up with its scroller's edge, which a sticky table
      // header covers; step back by whatever still hides the row's top.
      const hidden = visibleBounds(el).top - el.getBoundingClientRect().top;
      const scroller = scrollParent(el);
      if (hidden > 0 && scroller) {
        scroller.scrollTop -= hidden;
      }
    }));
  }
}

/** Nearest ancestor that scrolls vertically, if any. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
  }

  return null;
}

/**
 * The band of the window where `el` can actually be seen: clipped by every
 * scrolling ancestor (the table and list viewports sit below the page toolbar,
 * not at the top of the window) and, in the column layout, below the table's
 * sticky header.
 */
function visibleBounds(el: HTMLElement): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      const box = node.getBoundingClientRect();
      top = Math.max(top, box.top);
      bottom = Math.min(bottom, box.bottom);
    }
  }
  const head = el.closest('table')?.querySelector('thead');
  if (head && getComputedStyle(head).position === 'sticky') {
    top = Math.max(top, head.getBoundingClientRect().bottom);
  }

  return { top, bottom };
}
